import { getTenantId } from './config';
import { getCurrentLanguage } from './i18n';
import { apiFetch, dynamicCacheTtlMs } from './api';
import type { MiniAppConfig, MiniAppService, SupportedLanguage } from '@revio/shared';

export interface MiniAppConfigApiResponse {
  industry: string;
  config: MiniAppConfig;
  isCustomized: boolean;
}

// Memory Cache
let cachedMiniAppConfig: MiniAppConfigApiResponse | null = null;
let lastFetchedTime = 0;

/**
 * Fetch resolved MiniApp configuration for current tenant
 */
export async function fetchMiniAppConfig(): Promise<MiniAppConfigApiResponse | null> {
  const tenantId = getTenantId();
  if (!tenantId) return null;

  if (cachedMiniAppConfig && Date.now() - lastFetchedTime < dynamicCacheTtlMs) {
    return cachedMiniAppConfig;
  }

  try {
    const lang = getCurrentLanguage();
    const res = await apiFetch<{ success: boolean; data: MiniAppConfigApiResponse }>(
      `/api/v1/${tenantId}/miniapp-config?lang=${lang}`
    );

    if (res && res.success && res.data) {
      cachedMiniAppConfig = res.data;
      lastFetchedTime = Date.now();
      return cachedMiniAppConfig;
    }
    return null;
  } catch (err) {
    console.error('[miniapp-config] Failed to fetch miniapp config:', err);
    return cachedMiniAppConfig;
  }
}

/**
 * Get currently cached MiniApp config synchronously
 */
export function getCachedMiniAppConfig(): MiniAppConfigApiResponse | null {
  return cachedMiniAppConfig;
}

/**
 * Get active services list from config (or null if not loaded)
 */
export function getConfigServices(): MiniAppService[] {
  return cachedMiniAppConfig?.config?.services || [];
}

/**
 * Resolve terminology string for a given key, with service-level override
 */
export function term(key: string, serviceId?: string, fallback = ''): string {
  const lang = getCurrentLanguage() as SupportedLanguage;
  const config = cachedMiniAppConfig?.config;
  if (!config) return fallback;

  if (serviceId) {
    const service = config.services.find((s) => s.id === serviceId);
    if (service?.terminology && service.terminology[key]) {
      const val = service.terminology[key][lang] || service.terminology[key].en || service.terminology[key].th;
      if (val) return val;
    }
  }

  if (config.terminology && config.terminology[key]) {
    const val = config.terminology[key][lang] || config.terminology[key].en || config.terminology[key].th;
    if (val) return val;
  }

  return fallback;
}
